/**
 * Shopify Places Autocomplete JavaScript Class
 * Compatible with Shopify themes and handles CORS properly
 * 
 * Usage:
 * 1. Add this script to your theme's assets folder
 * 2. Include it in your theme.liquid: {{ 'shopify-places-autocomplete.js' | asset_url | script_tag }}
 * 3. Use the ShopifyPlacesAutocomplete class
 */

class ShopifyPlacesAutocomplete {
    constructor(inputId, options = {}) {
        this.input = document.getElementById(inputId);
        if (!this.input) {
            console.error('Places autocomplete input not found:', inputId);
            return;
        }

        // Reuse existing instance for this input to avoid duplicate listeners/requests.
        if (this.input.__placesAutocompleteInstance) {
            return this.input.__placesAutocompleteInstance;
        }

        // Configuration
        this.apiBaseUrl = this.input.dataset.apiUrl || options.apiBaseUrl || 'https://python-demo-875152285307.northamerica-northeast2.run.app';
        this.apiKey = this.input.dataset.apiKey || options.apiKey || '';
        this.debounceTime = options.debounceTime || 300;
        this.minQueryLength = options.minQueryLength || 3;
        
        // Elements
        this.resultsContainer = document.getElementById(inputId.replace('input', 'results'));
        this.selectedPlace = null;
        this.activeController = null;
        this.requestCounter = 0;
        this.requestTimeoutMs = options.requestTimeoutMs || 8000;
        this.lastQuery = '';
        this.lastPredictions = null;
        
        // Initialize
        this.init();
        this.input.dataset.initialized = 'true';
        this.input.__placesAutocompleteInstance = this;
    }
    
    init() {
        if (!this.resultsContainer) {
            console.error('Results container not found');
            return;
        }
        
        this.input.addEventListener('input', this.debounce(this.handleInput.bind(this), this.debounceTime));

        // Re-show cached results on focus instead of re-fetching. Mobile keyboards fire
        // repeated focus events (viewport resize); re-fetching here aborted the live request
        // and left "Finding businesses..." stuck with no list.
        this.input.addEventListener('focus', () => {
            if (this.lastPredictions && this.input.value.trim() === this.lastQuery) {
                this.showResults(this.lastPredictions);
            }
        });

        // Keyboard navigation
        this.input.addEventListener('keydown', this.handleKeydown.bind(this));

        // Close only on an explicit press outside the widget. `blur` is intentionally not used
        // because it fires unpredictably on mobile when the on-screen keyboard opens/closes.
        this._onOutsidePointer = (event) => {
            if (event.target === this.input) return;
            if (this.resultsContainer.contains(event.target)) return;
            this.hideResults();
        };
        document.addEventListener('pointerdown', this._onOutsidePointer);

        console.log('Shopify Places Autocomplete initialized for:', this.input.id);
    }
    
    async handleInput(event) {
        const query = event.target.value.trim();
        
        if (query.length < this.minQueryLength) {
            if (this.activeController) {
                this.activeController.abort();
                this.activeController = null;
            }
            this.lastQuery = '';
            this.lastPredictions = null;
            this.hideResults();
            return;
        }
        
        const requestId = ++this.requestCounter;
        this.showLoading();
        let timeoutId = null;
        let didTimeout = false;
        let controller = null;
        
        try {
            const url = `${this.apiBaseUrl}/api/places/autocomplete?input=${encodeURIComponent(query)}`;
            const headers = {
                'Content-Type': 'application/json'
            };
            
            // Add API key if provided
            if (this.apiKey) {
                headers['x-api-key'] = this.apiKey;
            }

            if (this.activeController) {
                this.activeController.abort();
            }
            controller = new AbortController();
            this.activeController = controller;
            timeoutId = setTimeout(() => {
                didTimeout = true;
                controller.abort();
            }, this.requestTimeoutMs);
            
            const response = await fetch(url, {
                method: 'GET',
                headers: headers,
                mode: 'cors', // Important for Shopify CORS
                signal: controller.signal
            });

            if (requestId !== this.requestCounter) {
                return;
            }
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const data = await response.json();
            
            if (data.status === 'success' && data.predictions) {
                this.lastQuery = query;
                this.lastPredictions = data.predictions;
                this.showResults(data.predictions);
            } else {
                this.lastPredictions = null;
                this.showError(data.error || 'No results found');
            }
            
        } catch (error) {
            if (error && error.name === 'AbortError') {
                // Ignore stale or user-interrupted requests; only show timeout for latest timed-out request.
                if (requestId === this.requestCounter && didTimeout) {
                    this.showError('Search timed out. Please try again.');
                }
                return;
            }
            console.error('Places API error:', error);
            this.showError('Failed to fetch places. Please try again.');
        } finally {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
            if (this.activeController === controller) {
                this.activeController = null;
            }
        }
    }
    
    showLoading() {
        this.resultsContainer.innerHTML = '<div class="signamic-loading places-loading">Finding businesses...</div>';
        this.resultsContainer.style.display = 'block';
    }
    
    showError(message) {
        this.resultsContainer.innerHTML = `<div class="signamic-error places-error">${message}</div>`;
        this.resultsContainer.style.display = 'block';
    }
    
    showResults(predictions) {
        this.resultsContainer.innerHTML = '';
        
        if (predictions.length === 0) {
            this.showError('No businesses found');
            return;
        }
        
        predictions.forEach((prediction, index) => {
            const item = document.createElement('div');
            item.className = 'signamic-search__result-item places-result-item';
            item.dataset.index = index;
            
            item.innerHTML = `
                <div class="signamic-result-content">
                    <div class="signamic-search__result-main places-main-text">${this.escapeHtml(prediction.main_text || '')}</div>
                    <div class="signamic-search__result-secondary places-secondary-text">${this.escapeHtml(prediction.secondary_text || '')}</div>
                </div>
            `;
            
            item.addEventListener('click', () => {
                this.selectPlace(prediction);
            });

            item.addEventListener('mouseenter', () => {
                this.clearHighlight();
                item.classList.add('highlighted');
            });
            
            this.resultsContainer.appendChild(item);
        });
        
        this.resultsContainer.style.display = 'block';
    }
    
    hideResults() {
        this.resultsContainer.style.display = 'none';
        this.clearHighlight();
    }
    
    selectPlace(place) {
        this.input.value = place.description;
        this.selectedPlace = place;
        this.lastQuery = '';
        this.lastPredictions = null;
        this.hideResults();
        
        // Trigger custom events for Shopify integration
        this.input.dispatchEvent(new CustomEvent('placeSelected', {
            detail: place,
            bubbles: true
        }));
        
        // Also trigger a generic change event
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
        
        console.log('Selected place:', place);
    }
    
    // Keyboard navigation
    handleKeydown(event) {
        const results = this.resultsContainer.querySelectorAll('.signamic-search__result-item');
        const highlighted = this.resultsContainer.querySelector('.signamic-search__result-item.highlighted');
        
        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault();
                if (results.length > 0) {
                    const next = highlighted ? 
                        highlighted.nextElementSibling || results[0] : 
                        results[0];
                    this.highlight(next);
                }
                break;
                
            case 'ArrowUp':
                event.preventDefault();
                if (results.length > 0) {
                    const prev = highlighted ? 
                        highlighted.previousElementSibling || results[results.length - 1] : 
                        results[results.length - 1];
                    this.highlight(prev);
                }
                break;
                
            case 'Enter':
                event.preventDefault();
                if (highlighted) {
                    highlighted.click();
                }
                break;
                
            case 'Escape':
                this.hideResults();
                break;
        }
    }
    
    highlight(item) {
        this.clearHighlight();
        item.classList.add('highlighted');
        item.scrollIntoView({ block: 'nearest' });
    }
    
    clearHighlight() {
        const highlighted = this.resultsContainer.querySelector('.signamic-search__result-item.highlighted, .places-result-item.highlighted');
        if (highlighted) {
            highlighted.classList.remove('highlighted');
        }
    }
    

    
    // Get place details
    async getPlaceDetails(placeId) {
        try {
            const url = `${this.apiBaseUrl}/api/places/details?place_id=${encodeURIComponent(placeId)}`;
            const headers = {
                'Content-Type': 'application/json'
            };
            
            if (this.apiKey) {
                headers['x-api-key'] = this.apiKey;
            }
            
            const response = await fetch(url, {
                method: 'GET',
                headers: headers,
                mode: 'cors'
            });
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const data = await response.json();
            return data.status === 'success' ? data.place : null;
            
        } catch (error) {
            console.error('Place details error:', error);
            return null;
        }
    }
    
    // Get place reviews
    async getPlaceReviews(placeId) {
        try {
            const url = `${this.apiBaseUrl}/api/places/reviews?place_id=${encodeURIComponent(placeId)}`;
            const headers = {
                'Content-Type': 'application/json'
            };
            
            if (this.apiKey) {
                headers['x-api-key'] = this.apiKey;
            }
            
            const response = await fetch(url, {
                method: 'GET',
                headers: headers,
                mode: 'cors'
            });
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const data = await response.json();
            return data.status === 'success' ? data : null;
            
        } catch (error) {
            console.error('Place reviews error:', error);
            return null;
        }
    }
    
    // Generate sign HTML with dynamic reviews
    async generateSign(placeId) {
        try {
            const url = `${this.apiBaseUrl}/api/places/sign?place_id=${encodeURIComponent(placeId)}`;
            const headers = {
                'Content-Type': 'application/json'
            };
            
            if (this.apiKey) {
                headers['x-api-key'] = this.apiKey;
            }
            
            const response = await fetch(url, {
                method: 'GET',
                headers: headers,
                mode: 'cors'
            });
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const data = await response.json();
            
            if (data.status === 'success' && data.sign_html) {
                // Store dynamic reviews globally for the sign to use
                if (data.reviews && Array.isArray(data.reviews)) {
                    window.DYNAMIC_REVIEWS = data.reviews;
                    window.PLACE_ID = placeId;
                    console.log('Dynamic reviews loaded:', data.reviews);
                }
                return data.sign_html;
            }
            
            return null;
            
        } catch (error) {
            console.error('Sign generation error:', error);
            return null;
        }
    }
    
    // Utility functions
    debounce(func, wait) {
        let timeout;
        return function executedFunction(...args) {
            const later = () => {
                clearTimeout(timeout);
                func(...args);
            };
            clearTimeout(timeout);
            timeout = setTimeout(later, wait);
        };
    }
    
    escapeHtml(text) {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    }
    
    // Public methods for Shopify integration
    getSelectedPlace() {
        return this.selectedPlace;
    }
    
    clearSelection() {
        this.selectedPlace = null;
        this.input.value = '';
        this.hideResults();
    }
    
    setValue(value) {
        this.input.value = value;
    }
}

// Global namespace for Shopify
window.ShopifyPlacesAutocomplete = ShopifyPlacesAutocomplete;

// Auto-initialize if data attributes are present
document.addEventListener('DOMContentLoaded', function() {
    const inputs = document.querySelectorAll('input[data-api-url]');
    inputs.forEach(input => {
        if (input.id && !input.dataset.initialized) {
            new ShopifyPlacesAutocomplete(input.id);
            input.dataset.initialized = 'true';
        }
    });
});

// CSS for keyboard navigation (inject if not present)
const style = document.createElement('style');
style.textContent = `
    .signamic-search__result-item.highlighted,
    .places-result-item.highlighted {
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
        color: white !important;
        transform: translateX(8px) scale(1.02) !important;
    }
    
    .signamic-search__result-item.highlighted .signamic-search__result-main,
    .signamic-search__result-item.highlighted .signamic-search__result-secondary,
    .places-result-item.highlighted .places-secondary-text {
        color: white !important;
    }
    
    .signamic-search__result-item.highlighted .signamic-search__result-secondary {
        opacity: 0.9 !important;
    }
`;
document.head.appendChild(style);


// Helper function to animate sign reviews
window.runSignReviewAnimation = function(options) {
    // options: { reviews, elementId, fadeDuration, intervalDuration }
    const reviews = options.reviews || window.DYNAMIC_REVIEWS || [];
    const elementId = options.elementId || ('review-text-' + (window.PLACE_ID || ''));
    const fadeDuration = options.fadeDuration || 1000; // ms
    const intervalDuration = options.intervalDuration || 4000; // ms
    setTimeout(function() {
        const reviewElement = document.getElementById(elementId);
        let currentIndex = 0;
        if (reviewElement && reviews.length > 0) {
            reviewElement.style.transition = 'opacity 1s ease-in-out';
            if (reviews.length > 1) {
                reviewElement.textContent = reviews[0];
                const intervalId = setInterval(function() {
                    currentIndex = (currentIndex + 1) % reviews.length;
                    reviewElement.style.opacity = '0';
                    setTimeout(function() {
                        reviewElement.textContent = reviews[currentIndex];
                        reviewElement.style.opacity = '1';
                    }, fadeDuration);
                }, intervalDuration);
                // Optionally store intervalId for later clearing
                reviewElement.dataset.reviewIntervalId = intervalId;
            } else {
                reviewElement.textContent = reviews[0];
            }
        }
    }, 100);
};